import { useState, useEffect } from "react";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Progress } from "@/components/ui/progress";
import { Badge } from "@/components/ui/badge";
import { supabase } from "@/integrations/supabase/client";
import { format } from "date-fns";
import { de } from "date-fns/locale";

interface SurveyQuestion {
  id: string;
  question: string;
  type: "rating" | "text";
  required: boolean;
}

interface SurveyAnswer {
  questionId: string;
  value: number | string;
}

interface SurveyResponse {
  id: string;
  answers: SurveyAnswer[];
  submitted_at: string;
}

interface SurveySettings {
  id: string;
  title: string;
  questions: SurveyQuestion[];
}

interface QuestionStats {
  question: SurveyQuestion;
  averageRating?: number;
  ratingCounts?: { [key: number]: number };
  textResponses?: string[];
  totalResponses: number;
}

const SurveyResults = () => {
  const [settings, setSettings] = useState<SurveySettings | null>(null);
  const [responses, setResponses] = useState<SurveyResponse[]>([]);
  const [loading, setLoading] = useState(true);
  const [stats, setStats] = useState<QuestionStats[]>([]);

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    try {
      // Load settings
      const { data: settingsData, error: settingsError } = await supabase
        .from('survey_settings')
        .select('id, title, questions')
        .limit(1)
        .maybeSingle();

      if (settingsError) throw settingsError;

      if (settingsData) {
        const parsedSettings = {
          ...settingsData,
          questions: (settingsData.questions as unknown as SurveyQuestion[]) || []
        };
        setSettings(parsedSettings);

        // Load responses
        const { data: responsesData, error: responsesError } = await supabase
          .from('survey_responses')
          .select('*')
          .eq('survey_settings_id', settingsData.id)
          .order('submitted_at', { ascending: false });

        if (responsesError) throw responsesError;

        const parsedResponses = (responsesData || []).map(r => ({
          ...r,
          answers: (r.answers as unknown as SurveyAnswer[]) || []
        }));
        setResponses(parsedResponses);

        // Calculate stats
        calculateStats(parsedSettings.questions, parsedResponses);
      }
    } catch (error) {
      console.error('Error loading survey data:', error);
    } finally {
      setLoading(false);
    }
  };

  const calculateStats = (questions: SurveyQuestion[], responses: SurveyResponse[]) => {
    const questionStats: QuestionStats[] = questions.map(question => {
      const answersForQuestion = responses
        .flatMap(r => r.answers)
        .filter(a => a.questionId === question.id);

      if (question.type === 'rating') {
        const ratings = answersForQuestion
          .map(a => typeof a.value === 'number' ? a.value : parseInt(String(a.value)))
          .filter(r => !isNaN(r));

        const ratingCounts: { [key: number]: number } = { 1: 0, 2: 0, 3: 0, 4: 0, 5: 0 };
        ratings.forEach(r => {
          if (r >= 1 && r <= 5) ratingCounts[r]++;
        });

        const average = ratings.length > 0 
          ? ratings.reduce((a, b) => a + b, 0) / ratings.length 
          : 0;

        return {
          question,
          averageRating: average,
          ratingCounts,
          totalResponses: ratings.length
        };
      } else {
        const textResponses = answersForQuestion
          .map(a => String(a.value))
          .filter(t => t.trim().length > 0);

        return {
          question,
          textResponses,
          totalResponses: textResponses.length
        };
      }
    });

    setStats(questionStats);
  };

  const getRatingColor = (avg: number) => {
    if (avg >= 4) return 'text-green-600';
    if (avg >= 3) return 'text-yellow-600';
    return 'text-red-600';
  };

  const getRatingLabel = (avg: number) => {
    if (avg >= 4.5) return 'Sehr gut';
    if (avg >= 3.5) return 'Gut';
    if (avg >= 2.5) return 'Neutral';
    if (avg >= 1.5) return 'Verbesserungswürdig';
    return 'Kritisch';
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-muted-foreground">Wird geladen...</div>
      </div>
    );
  }

  if (!settings) {
    return (
      <Card>
        <CardContent className="pt-6">
          <p className="text-muted-foreground">Keine Umfrage-Einstellungen gefunden</p>
        </CardContent>
      </Card>
    );
  }

  return (
    <div className="space-y-6">
      {/* Overview */}
      <Card>
        <CardHeader>
          <CardTitle>Umfrage-Auswertung</CardTitle>
          <CardDescription>
            Anonyme Auswertung der eingegangenen Antworten
          </CardDescription>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div className="text-center p-4 bg-secondary rounded-lg">
              <div className="text-3xl font-bold">{responses.length}</div>
              <div className="text-sm text-muted-foreground">Eingegangene Antworten</div>
            </div>
            <div className="text-center p-4 bg-secondary rounded-lg">
              <div className="text-3xl font-bold">{settings.questions.length}</div>
              <div className="text-sm text-muted-foreground">Fragen</div>
            </div>
            <div className="text-center p-4 bg-secondary rounded-lg">
              <div className="text-3xl font-bold">
                {responses.length > 0 
                  ? format(new Date(responses[0].submitted_at), 'dd.MM.yy', { locale: de })
                  : '-'
                }
              </div>
              <div className="text-sm text-muted-foreground">Letzte Antwort</div>
            </div>
          </div>
        </CardContent>
      </Card>

      {responses.length === 0 ? (
        <Card>
          <CardContent className="pt-6 text-center text-muted-foreground">
            Noch keine Antworten eingegangen
          </CardContent>
        </Card>
      ) : (
        <>
          {/* Rating Questions */}
          {stats.filter(s => s.question.type === 'rating').map((stat, index) => (
            <Card key={stat.question.id}>
              <CardHeader>
                <div className="flex items-start justify-between">
                  <div>
                    <CardTitle className="text-base">{stat.question.question}</CardTitle>
                    <CardDescription>{stat.totalResponses} Antworten</CardDescription>
                  </div>
                  <div className="text-right">
                    <div className={`text-2xl font-bold ${getRatingColor(stat.averageRating || 0)}`}>
                      {(stat.averageRating || 0).toFixed(1)}
                    </div>
                    <Badge variant="outline">{getRatingLabel(stat.averageRating || 0)}</Badge>
                  </div>
                </div>
              </CardHeader>
              <CardContent>
                <div className="space-y-3">
                  {[5, 4, 3, 2, 1].map(rating => {
                    const count = stat.ratingCounts?.[rating] || 0;
                    const percentage = stat.totalResponses > 0 
                      ? (count / stat.totalResponses) * 100 
                      : 0;
                    
                    return (
                      <div key={rating} className="flex items-center gap-3">
                        <span className="w-6 text-sm text-muted-foreground">{rating}</span>
                        <Progress value={percentage} className="flex-1 h-3" />
                        <span className="w-12 text-sm text-right text-muted-foreground">
                          {count} ({percentage.toFixed(0)}%)
                        </span>
                      </div>
                    );
                  })}
                </div>
              </CardContent>
            </Card>
          ))}

          {/* Text Questions */}
          {stats.filter(s => s.question.type === 'text' && (s.textResponses?.length || 0) > 0).map(stat => (
            <Card key={stat.question.id}>
              <CardHeader>
                <CardTitle className="text-base">{stat.question.question}</CardTitle>
                <CardDescription>{stat.totalResponses} Antworten (Freitext)</CardDescription>
              </CardHeader>
              <CardContent>
                <div className="space-y-3 max-h-80 overflow-y-auto">
                  {stat.textResponses?.map((response, idx) => (
                    <div key={idx} className="p-3 bg-secondary rounded-lg text-sm">
                      "{response}"
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>
          ))}
        </>
      )}
    </div>
  );
};

export default SurveyResults;
